/**
 * Visual source pane (S8) — hosts the SAME `CompactVqeContent` surface the
 * Query Editor's compact popover uses, wired by `useAddTileVqe` to the
 * dialog's always-mounted (hidden) SqlMonaco. The buffer stays the source of
 * truth: visual edits commit to the editor, flow back through its onChange
 * into the dialog's `sql` state, and the SQL tab shows exactly what will run.
 * The compact footer's Done / "Open full editor" row is hidden here
 * (`hideFooterActions`) — the dialog's own Preview / Add tile buttons are the
 * confirmation surface, and the SQL tab is one tab-click away. The header's
 * close (X) still flips to the SQL tab; degrade cards keep their own
 * full-editor escape CTA.
 */
import { useEffect, useRef } from 'react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { sqlEngineToDatabaseEngineId } from '@/core/engines/sqlEngineToDatabaseEngineId';
import { useConnectionsUIStore } from '../../../../connections/store/uiStore';
import { CompactVqeContent } from '../../../visual-query-editor/compact/CompactVqeContent';
import { useCompactDrill } from '../../../visual-query-editor/compact/useCompactDrill';
import { useVqeStore } from '../../../visual-query-editor/store/vqeStore';
import { useCommitFeedbackToasts } from '../../../visual-query-editor/ux/useCommitFeedbackToasts';
import type { AddTileVqe } from './useAddTileVqe';
import { ADD_TILE_VQE_TAB_ID } from './useAddTileVqe';
import { VisualStarter } from './VisualStarter';

export function VisualSourcePane({
  vqe,
  sql,
  connectionId,
  database,
  onSeedSql,
  onShowSql,
  onOpenFull,
}: {
  vqe: AddTileVqe;
  sql: string;
  connectionId: string;
  database?: string;
  /** Starter seed (`SELECT * FROM <table>`) — visual provenance in the dialog. */
  onSeedSql: (sql: string) => void;
  /** The surface's "Done"/close affordance — flips the dialog to the SQL tab. */
  onShowSql: () => void;
  /** Promote to a full Visual Query Editor tab (dialog closes). */
  onOpenFull: () => void;
}) {
  const { track } = useAnalytics();
  // Commit-failure toasts for edits made on this surface (multi-mount-safe).
  useCommitFeedbackToasts();
  const drill = useCompactDrill();
  const bannerDismissed = useVqeStore((s) => s.dismissedBanners.has(ADD_TILE_VQE_TAB_ID));
  const dismissBanner = useVqeStore((s) => s.dismissBanner);
  const engineId = connectionId ? sqlEngineToDatabaseEngineId(vqe.options.engine) : undefined;

  const openedRef = useRef(false);
  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    track('feature:dashboard_vqe_source_open', engineId ? { engine: engineId } : {});
  }, [track, engineId]);

  // Empty buffer → the compact surface has no statement to edit; bootstrap
  // with the starter (pick a table → seeded SELECT flows back as visual SQL).
  if (!sql.trim()) {
    return (
      <div
        className="flex h-72 shrink-0 flex-col overflow-hidden rounded-md border border-border"
        data-testid="add-tile-visual-pane"
      >
        <VisualStarter
          connectionId={connectionId}
          database={database}
          onSeedSql={(seeded) => {
            onSeedSql(seeded);
            track('feature:dashboard_vqe_canvas_seed', engineId ? { engine: engineId } : {});
          }}
          onShowSql={onShowSql}
        />
      </div>
    );
  }

  return (
    <div
      className="flex h-72 shrink-0 flex-col overflow-hidden rounded-md border border-border bg-popover"
      data-testid="add-tile-visual-pane"
    >
      <TooltipProvider>
        <CompactVqeContent
          summary={vqe.summary}
          mode={vqe.mode}
          options={vqe.options}
          editContext={vqe.editContext}
          onOpenFull={onOpenFull}
          onClose={onShowSql}
          bannerState={vqe.bannerState}
          bannerDismissed={bannerDismissed}
          onBannerDismiss={() => dismissBanner(ADD_TILE_VQE_TAB_ID)}
          onBannerPrimaryAction={
            vqe.bannerState === 'read-only'
              ? undefined
              : () => {
                  if (vqe.bannerState === 'no-connection') {
                    useConnectionsUIStore.getState().setShowConnectionsDialog(true);
                  }
                }
          }
          drill={drill}
          tabId={ADD_TILE_VQE_TAB_ID}
          connectionId={connectionId || null}
          engineId={engineId}
          database={database}
          hideFooterActions
        />
      </TooltipProvider>
    </div>
  );
}
